import React, { useState, useEffect } from "react";
import { FaCheckDouble } from "react-icons/fa6";
import { FiRotateCw } from "react-icons/fi";
import "./AddRolePanel.css";
import Services from "../Services/WebService";
import { toast } from "react-toastify"; 
import CrossButton from "../Customs/Button/CrossButton";

type PermissionKey = "read" | "write" | "update" | "delete" | "all";

interface Module {
  menu: string;
  module_id: number;
  module_code?: string;
  module_path?: string;
}

interface AddRolePanelProps {
  open: boolean;
  onClose: () => void;
  onRoleAdded?: () => void;
  editingRole?: any;
  editingRoleDetails?: any[];
  isFetchingDetails?: boolean;
}

interface PermissionState {
  read: boolean;
  write: boolean;
  update: boolean;
  delete: boolean;
  all: boolean;
}

const getInitialPermissions = (moduleList: Module[]) =>
  moduleList.map(() => ({
    read: false,
    write: false,
    update: false,
    delete: false,
    all: false,
  }));

const AddRolePanel: React.FC<AddRolePanelProps> = ({
  open,
  onClose,
  onRoleAdded,
  editingRole,
  editingRoleDetails,
  isFetchingDetails,
}) => {
  const [roleName, setRoleName] = useState("");
  const [modules, setModules] = useState<Module[]>([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [permissions, setPermissions] = useState<PermissionState[]>([]);
  const [checkAll, setCheckAll] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load modules and prefill permissions if editing
  useEffect(() => {
    if (open) {
      setCheckAll(false);
      setModulesLoading(true);

      Services.getAPI({
        action: "/v1/door-management/rbac/modules",
      })
        .then((response) => {
          const res = response as any;
          const arr = Array.isArray(res.data) ? res.data : [];
          if (arr.length > 0) {
            const fetchedModules = arr.map((mod: any) => ({
              menu: mod.module_name,
              module_id: mod.module_id,
              module_code: mod.module_code,
              module_path: mod.module_path,
            }));
            setModules(fetchedModules);

            // Prefill permissions for edit mode
            if (editingRole && editingRoleDetails && editingRoleDetails.length > 0) {
              const rolePerms: PermissionState[] = fetchedModules.map((mod: Module) => {
                const perm = editingRoleDetails.find((p: any) => p.code === mod.module_code);
                if (perm) {
                  const allChecked = perm.read && perm.write && perm.update && perm.delete;
                  return {
                    read: !!perm.read,
                    write: !!perm.write,
                    update: !!perm.update,
                    delete: !!perm.delete,
                    all: allChecked,
                  };
                }
                return { read: false, write: false, update: false, delete: false, all: false };
              });
              setPermissions(rolePerms);
              setRoleName(editingRole.role_name || "");
              setCheckAll(rolePerms.length > 0 && rolePerms.every(row => row.all));
            } else {
              setPermissions(getInitialPermissions(fetchedModules));
              setRoleName("");
            }
          } else {
            setModules([]);
            setPermissions([]);
          }
        })
        .catch(() => {
          setModules([]);
          setPermissions([]);
          toast.error("Failed to fetch modules");
        })
        .finally(() => setModulesLoading(false));
    } else {
      setRoleName("");
      setModules([]);
      setPermissions([]);
      setCheckAll(false);
    }
  }, [open, editingRole, editingRoleDetails]);

  // Checkbox logic
  const handleRowChange = (rowIdx: number, key: PermissionKey, value: boolean) => {
    const newPermissions = [...permissions];
    if (key === "all") {
      newPermissions[rowIdx] = {
        read: value,
        write: value,
        update: value,
        delete: value,
        all: value,
      };
    } else {
      newPermissions[rowIdx][key] = value;
      const allChecked =
        newPermissions[rowIdx].read &&
        newPermissions[rowIdx].write &&
        newPermissions[rowIdx].update &&
        newPermissions[rowIdx].delete;
      newPermissions[rowIdx].all = allChecked;
    }
    setPermissions(newPermissions);
    setCheckAll(newPermissions.length > 0 && newPermissions.every(row => row.all));
  };

  const handleCheckAllPermissions = () => {
    const next = !checkAll;
    setPermissions(
      permissions.map(() => ({
        read: next,
        write: next,
        update: next,
        delete: next,
        all: next,
      }))
    );
    setCheckAll(next);
  };

  const handleReset = () => {
    setRoleName(editingRole ? editingRole.role_name || "" : "");
    setPermissions(editingRole && editingRoleDetails && editingRoleDetails.length > 0
      ? modules.map((mod, idx) => {
        const perm = editingRoleDetails.find((p: any) => p.code === mod.module_code);
        if (perm) {
          const allChecked = perm.read && perm.write && perm.update && perm.delete;
          return {
            read: !!perm.read,
            write: !!perm.write,
            update: !!perm.update,
            delete: !!perm.delete,
            all: allChecked,
          };
        }
        return { read: false, write: false, update: false, delete: false, all: false };
      })
      : getInitialPermissions(modules));
    setCheckAll(false);
  };

 
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if ((e.target as HTMLElement).classList.contains("add-role-modal-overlay")) {
      handleReset();
      onClose();
    }
  };

  // Save role handler
  const handleSaveRole = async () => {
    if (!roleName.trim()) {
      toast.error("Please enter a role name.");
      return;
    }
    setIsSaving(true);
    try {
    
      const payload: any = {
        role_name: roleName,
        modules_permissions: permissions.map((perm, idx) => ({
          module_id: modules[idx]?.module_id,
          read: perm.read,
          write: perm.write,
          update: perm.update,
          delete: perm.delete,
        })),
      };
      if (editingRole && editingRole.role_id) {
        payload.role_id = editingRole.role_id;
      }

      await Services.postAPI({
        action: "/v1/door-management/rbac/roles",
        body: payload,
      });

      toast.success(editingRole ? "Role updated successfully!" : "Role added successfully!");

      if (onRoleAdded) onRoleAdded();
      handleReset();
      onClose();
    } catch (err) {
      toast.error("Failed to save role. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div
        className={`add-role-modal-overlay${open ? " open" : " closed"}`}
        onClick={handleOverlayClick}
      />

      <div
        className={`add-role-panel${open ? " open" : ""}`}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        style={{
          width: "100%",
          maxWidth: "775px",
          height: "100vh",
          borderTopRightRadius: "16px",
          borderBottomRightRadius: "16px",
          background: "#fff",
          boxShadow: "-2px 0 32px 0 rgba(0,0,0,0.07)",
          right: open ? "0" : "-800px",
          transition: "right 0.5s cubic-bezier(0.33, 1, 0.68, 1)",
          position: "fixed",
          top: 0,
          zIndex: 2002,
        }}
      >
        <div className="panel-header">
          <span className="panel-title">{editingRole ? "Edit Role" : "Add new role"}</span>
<CrossButton onClick={() => { handleReset(); onClose(); }} className="panel-close" />

        </div>

        <div className="panel-content">
          <div className="role-inputs-row">
            <input
              className="role-name-input"
              type="text"
              value={roleName}
              onChange={e => setRoleName(e.target.value)}
              placeholder="Enter role name"
              disabled={!!isFetchingDetails}
            />
            <button className="reset-btn" onClick={handleReset} title="Reset" type="button" disabled={!!isFetchingDetails}>
              <FiRotateCw size={20} />
            </button>
            <button
              className={`check-all-btn${checkAll ? " checked" : ""}`}
              onClick={handleCheckAllPermissions}
              type="button"
              disabled={modules.length === 0 || !!isFetchingDetails}
            >
              <FaCheckDouble className="checkall-icon" />
              Check All Permissions
            </button>
          </div>

          <div className="permission-table-wrapper">
            {modulesLoading || isFetchingDetails ? (
              <div style={{ padding: "1.5rem 0", textAlign: "center", color: "#888" }}>
                Loading modules...
              </div>
            ) : (
              <table className="permission-table">
                <thead>
                  <tr>
                    <th className="permission-table-header left-header">Menu</th>
                    <th className="permission-table-header">Read</th>
                    <th className="permission-table-header">Write</th>
                    <th className="permission-table-header">Update</th>
                    <th className="permission-table-header">Delete</th>
                    <th className="permission-table-header">All</th>
                  </tr>
                </thead>
                <tbody>
                  {modules.map((mod, idx) => (
                    <tr key={mod.module_id ?? idx}>
                      <td className="permission-menu-cell">
                        <span>{mod.menu}</span>
                      </td>
                      {["read", "write", "update", "delete", "all"].map((key) => (
                        <td key={key} className="checkbox-cell">
                          <input
                            type="checkbox"
                            className="custom-checkbox"
                            checked={permissions[idx]?.[key as PermissionKey] || false}
                            onChange={e =>
                              handleRowChange(idx, key as PermissionKey, e.target.checked)
                            }
                            aria-label={`${key} permission`}
                            disabled={!!isFetchingDetails}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                  {modules.length === 0 && !modulesLoading && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: "center", color: "#888" }}>
                        No modules found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          <button
            className="save-role-btn"
            type="button"
            onClick={handleSaveRole}
            disabled={isSaving || modules.length === 0 || !!isFetchingDetails}
          >
            {isSaving ? "Saving..." : editingRole ? "Update Role" : "Save Role"}
          </button>
        </div>
      </div>
    </>
  );
};

export default AddRolePanel;
